import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Calendar } from 'react-big-calendar';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useEffectOnce } from '@guryou/hooks';
import { ServiceTypes } from '@guryou/core';
import { DynamicThemeContext, gyLocalizer } from '@guryou/html-components';
import {
  getClassesForBooking,
  getPublicSessions,
  stateIsLoading,
  stateIsNotInitialized,
  useCalendarMessages,
  useCheckIsMobile,
  widgetGetIntegration,
} from '@guryou/redux';
import moment from 'moment';
import { ensureWidgetIntegrationGoogleFont } from '../../utils/widgetIntegrationGoogleFonts';
import CustomToolbar from '../../components/calendar/CustomToolbar';
import EventField from '../../components/calendar/EventField';
import MonthEventField from '../../components/calendar/MonthEventField';
import '../../assets/react-big-calendar.css';

const localizer = gyLocalizer(moment);

const SessionModal = ({ event, sessionDetail, loading, accent, onClose, t }) => {
  if (!event) return null;

  const { name, startTime, endTime, resource } = event;
  const price = resource?.offer?.price ?? resource?.session?.price;
  const sessionId = resource?.session?.id;

  const fullSession = sessionDetail?.find?.(s => s.id === sessionId);
  const available = fullSession?.availableCapacity;
  const booked = fullSession?.currentCapacity;
  const total = available != null && booked != null ? booked + available : null;
  const canBook = fullSession?.canBeBought;
  const status = fullSession?.status;

  const formatDateTime = val => {
    if (!val) return '—';
    return moment(val).format('ddd, D MMM YYYY · HH:mm');
  };

  const formatTime = val => (val ? moment(val).format('HH:mm') : '—');

  const overlayStyle = {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0,0,0,0.45)',
    zIndex: 2000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '16px',
    boxSizing: 'border-box',
  };

  const cardStyle = {
    backgroundColor: '#fff',
    borderRadius: '12px',
    padding: '24px',
    maxWidth: '360px',
    width: '100%',
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
    position: 'relative',
  };

  const rowStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 0',
    borderBottom: '1px solid #f0f0f0',
    fontSize: '14px',
  };

  const labelStyle = { color: '#888', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' };
  const valueStyle = { fontWeight: 600, color: '#1a1a1a' };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={cardStyle} onClick={e => e.stopPropagation()}>
        <button
          type="button"
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '12px',
            right: '12px',
            background: 'none',
            border: 'none',
            fontSize: '20px',
            cursor: 'pointer',
            color: '#888',
            lineHeight: 1,
            padding: '4px',
          }}
        >
          ×
        </button>

        <div style={{ marginBottom: '16px' }}>
          <div
            style={{
              width: '4px',
              height: '24px',
              backgroundColor: accent,
              borderRadius: '2px',
              display: 'inline-block',
              marginRight: '10px',
              verticalAlign: 'middle',
            }}
          />
          <span style={{ fontSize: '18px', fontWeight: 700, color: '#1a1a1a', verticalAlign: 'middle' }}>{name}</span>
        </div>

        <div style={rowStyle}>
          <span style={labelStyle}>{t('GENERAL.date', 'Data')}</span>
          <span style={valueStyle}>{formatDateTime(startTime)}</span>
        </div>

        <div style={rowStyle}>
          <span style={labelStyle}>{t('GENERAL.time', 'Horário')}</span>
          <span style={valueStyle}>
            {formatTime(startTime)} – {formatTime(endTime)}
          </span>
        </div>

        {price != null && price > 0 && (
          <div style={rowStyle}>
            <span style={labelStyle}>{t('GENERAL.price', 'Preço')}</span>
            <span style={{ ...valueStyle, color: accent }}>€ {Number(price).toFixed(2)}</span>
          </div>
        )}

        <div style={{ ...rowStyle, borderBottom: 'none' }}>
          <span style={labelStyle}>{t('GENERAL.availability', 'Disponibilidade')}</span>
          {loading ? (
            <span style={{ color: '#aaa', fontSize: '13px' }}>{t('GENERAL.loading', '...')}</span>
          ) : total != null ? (
            <span
              style={{
                ...valueStyle,
                color: available === 0 ? '#ef4444' : available <= 3 ? '#f97316' : '#22c55e',
              }}
            >
              {available === 0 ? t('GENERAL.full', 'Esgotado') : `${available} / ${total} ${t('GENERAL.available', 'disponíveis')}`}
            </span>
          ) : (
            <span style={{ color: '#aaa', fontSize: '13px' }}>—</span>
          )}
        </div>

        {canBook === false && (
          <div
            style={{
              marginTop: '12px',
              padding: '8px 12px',
              backgroundColor: '#fef2f2',
              borderRadius: '6px',
              fontSize: '12px',
              color: '#ef4444',
              textAlign: 'center',
            }}
          >
            {t('GENERAL.bookingClosed', 'Inscrições encerradas')}
          </div>
        )}
      </div>
    </div>
  );
};

const WidgetCalendar = () => {
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const { integrationId } = useParams();
  const { setMainColor } = useContext(DynamicThemeContext);
  const calendarMessages = useCalendarMessages();
  const isMobile = useCheckIsMobile();

  const [view, setView] = useState('month');
  const [date, setDate] = useState(new Date());
  const [selectedEvent, setSelectedEvent] = useState(null);

  const integration = useSelector(state => state.widget.integration);
  const classesForBooking = useSelector(state => state.widget.classesForBooking);
  const publicSessions = useSelector(state => state.widget.sessions);

  const { providerId, configuration, provider } = integration.data || {};
  const { showCourses, showEvents, primaryColor, fontFamily, fontStyle } = configuration || {};
  const providerName = provider?.name || null;

  useEffectOnce(() => {
    if (integrationId) dispatch(widgetGetIntegration(integrationId));
  });

  useEffect(() => {
    if (!providerId) return;
    dispatch(getClassesForBooking(providerId));
  }, [dispatch, providerId]);

  useEffect(() => {
    if (primaryColor) setMainColor(primaryColor);
  }, [primaryColor, setMainColor]);

  useEffect(() => {
    ensureWidgetIntegrationGoogleFont(fontFamily);
  }, [fontFamily]);

  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const root = document.getElementById('root');
    const prev = {
      htmlBg: html.style.backgroundColor,
      bodyBg: body.style.backgroundColor,
      rootBg: root?.style.backgroundColor ?? '',
    };
    html.style.backgroundColor = 'transparent';
    body.style.backgroundColor = 'transparent';
    if (root) root.style.backgroundColor = 'transparent';
    return () => {
      html.style.backgroundColor = prev.htmlBg;
      body.style.backgroundColor = prev.bodyBg;
      if (root) root.style.backgroundColor = prev.rootBg;
    };
  }, []);

  useEffect(() => {
    const calendarCulture = i18n.language?.toLowerCase().startsWith('en') ? 'en' : 'it';
    moment.locale(calendarCulture);
  }, [i18n.language]);

  const accent = primaryColor
    ? String(primaryColor)
        .trim()
        .startsWith('#')
      ? String(primaryColor).trim()
      : `#${String(primaryColor).trim()}`
    : '#e85d75';

  const events = useMemo(() => {
    if (!classesForBooking.data) return [];
    return classesForBooking.data
      .filter(cls => {
        if (cls.serviceType === ServiceTypes.course) return showCourses !== false;
        if (cls.serviceType === ServiceTypes.event) return showEvents !== false;
        return false;
      })
      .flatMap(cls =>
        (cls.offers || []).flatMap(offer =>
          (offer.sessions || []).map(session => ({
            start: new Date(session.startTime),
            end: new Date(session.endTime),
            name: cls.name,
            startTime: session.startTime,
            endTime: session.endTime,
            color: accent,
            resource: { cls, offer, session },
          }))
        )
      );
  }, [classesForBooking.data, showCourses, showEvents, accent]);

  const handleSelectEvent = event => {
    setSelectedEvent(event);
    dispatch(getPublicSessions(event.resource.cls.id));
  };

  const isLoading =
    stateIsNotInitialized(integration) ||
    stateIsLoading(integration) ||
    (!classesForBooking.data && (stateIsNotInitialized(classesForBooking) || stateIsLoading(classesForBooking)));

  const fontFamilyStyle = fontFamily && fontFamily !== 'Metropolis' ? { fontFamily } : {};
  let fontStyleCSS = {};
  if (fontStyle && fontStyle !== 'sans-serif') {
    if (fontStyle === 'italic') fontStyleCSS = { fontStyle: 'italic' };
    else if (fontStyle === 'bold' || fontStyle === 'Double-struck') fontStyleCSS = { fontWeight: 'bold' };
  }

  const views = isMobile ? ['day', 'agenda'] : ['month', 'week', 'day'];
  const calendarCulture = i18n.language?.toLowerCase().startsWith('en') ? 'en' : 'it';
  const sessionLoading = stateIsLoading(publicSessions);

  return (
    <div
      style={{
        width: '100%',
        minHeight: 680,
        padding: '16px',
        boxSizing: 'border-box',
        background: '#fff',
        ...fontFamilyStyle,
        ...fontStyleCSS,
      }}
    >
      <div className="bg-white rounded-xl border border-gray-200" style={{ padding: '16px', boxSizing: 'border-box', minHeight: 648 }}>
        {isLoading ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 600 }} />
        ) : (
          <Calendar
            localizer={localizer}
            events={events}
            views={views}
            view={view}
            date={date}
            onView={setView}
            onNavigate={setDate}
            culture={calendarCulture}
            messages={calendarMessages}
            startAccessor="start"
            endAccessor="end"
            style={{ height: 600 }}
            eventPropGetter={() => ({ style: { backgroundColor: 'transparent', border: 'none', padding: 0 } })}
            onSelectEvent={handleSelectEvent}
            components={{
              toolbar: props => <CustomToolbar {...props} centerLabel={providerName} isAllCenters={!providerName} />,
              event: EventField,
              month: { event: MonthEventField },
            }}
          />
        )}
      </div>

      <SessionModal
        event={selectedEvent}
        sessionDetail={publicSessions?.data}
        loading={sessionLoading}
        accent={accent}
        onClose={() => setSelectedEvent(null)}
        t={t}
      />
    </div>
  );
};

export default WidgetCalendar;
